import React from "react";
import { connect } from "react-redux";
import withStyles from "isomorphic-style-loader/withStyles";
import firebase from "firebase/compat/app";
import ReactTooltip from "react-tooltip";
import { charityRootConsoleUrl } from "../../../../utils/firebaseConsoleUrls";
import { isAdminDebugUiActive } from "../../../../utils/adminDebugUi";
import s from "./styles.scss";

interface Props {
  showQuantity: boolean;
  quantity: string;
  message: string;
  isAdminDebugUi?: boolean;
  currentCharityId?: string;
  stacked?: boolean;
}

const COUNT_SUFFIXES = ["", "k", "M", "B", "T"];

/** Formats a selection count to at most four characters, rounding down above 9999. */
export const formatCompactCount = (quantity: number): string => {
  if (quantity < 10000) {
    return String(quantity);
  }
  let tier = Math.floor(Math.log10(quantity) / 3);
  if (tier >= COUNT_SUFFIXES.length) {
    tier = COUNT_SUFFIXES.length - 1;
  }
  return `${Math.floor(quantity / 1000 ** tier)}${COUNT_SUFFIXES[tier]}`;
};

class Footer extends React.Component<Props> {
  render() {
    const mainProjectId = (firebase.app().options as {
      projectId?: string;
    }).projectId;
    const charityFirebaseHref =
      this.props.isAdminDebugUi &&
      this.props.currentCharityId &&
      mainProjectId
        ? charityRootConsoleUrl(mainProjectId, this.props.currentCharityId)
        : null;

    const exactQuantity = Number(this.props.quantity);
    const isNumericQuantity = Number.isFinite(exactQuantity);
    const exactLabel = isNumericQuantity
      ? exactQuantity.toLocaleString("en-US")
      : String(this.props.quantity);

    return (
      <div
        className={
          this.props.stacked ? `${s.root} ${s.stacked}` : s.root
        }
      >
        <div className={s.navRow}>
          <div className={s.footerLeft}>
            {this.props.showQuantity && (
              <div>
                <span
                  className={s.dot}
                  tabIndex={0}
                  aria-label={`${exactLabel} ${this.props.message}`}
                  data-tip
                  data-for="footer-selection-count"
                  data-event="mouseenter focus"
                  data-event-off="mouseleave blur"
                >
                  {isNumericQuantity
                    ? formatCompactCount(exactQuantity)
                    : this.props.quantity}
                </span>
                <ReactTooltip
                  id="footer-selection-count"
                  type="info"
                  place="top"
                  effect="solid"
                >
                  {exactLabel}
                </ReactTooltip>
                <span className={s.text}>{this.props.message}</span>
              </div>
            )}
          </div>
          <div className={s.footerCenter}>
            {charityFirebaseHref && (
              <a
                href={charityFirebaseHref}
                className={s.debugFirebaseLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {`Open ${this.props.currentCharityId} on Firebase`}
              </a>
            )}
          </div>
          <div className={s.footerRight}>
            <a
              href="http://viewspark.org/"
              className={s.logo}
              target="_blank"
              rel="noopener noreferrer"
            />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const { common, user } = state;
  return {
    showQuantity: common.showQuantity,
    quantity: common.quantity,
    message: common.message,
    isAdminDebugUi: isAdminDebugUiActive(user.isSuperadmin),
    currentCharityId: user.currentCharity && user.currentCharity.id,
  };
};

export default connect<Props>(mapStateToProps, null)(withStyles(s)(Footer));
